import type { IntentManagementState, Topic } from "../../../types/common";
import type { ActionType } from "typesafe-actions";
import { createReducer } from "typesafe-actions";
import * as ac from "app/pages/sales-intelligence/sub-modules/intent/store/management/actions/actions";
import { DEFAULT_LOCKED_PERIOD } from "app/pages/sales-intelligence/sub-modules/intent/types/constants";
import { getDifferenceDateByDay } from "app/pages/sales-intelligence/sub-modules/intent/helpers/utils";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";

export const INITIAL_INTENT_MANAGEMENT_STATE: IntentManagementState = {
    isCategoriesFetching: false,
    isCategoriesFetched: false,
    categoryFetchError: undefined,
    isTopicsFetching: false,
    isTopicsFetched: false,
    isSelectedTopicsFetching: false,
    isSelectedTopicsFetched: false,
    isLocked: false,
    isUserApprovedTerms: false,
    isUserApprovedTermsFetched: false,
    isScrollSelectedTopic: true,
    topicsFetchError: undefined,
    saveTopicsError: undefined,
    selectedTopicsFetchError: undefined,
    contactAdmins: [],
    categories: [],
    topics: [],
    totalCount: 0,
    createdAt: undefined,
    selectedTopics: [],
    searchObj: {
        categories: [],
        topics: [],
        asc: true,
        orderBy: "topic",
        searchText: "",
        pageSize: 100,
        page: 1,
    },
};

const hasAccessToManageIntentTopicsForAdmin = () => salesSettingsHelper.hasAccessToManageIntentTopicsForAdmin();

const intentManagementReducer = createReducer<IntentManagementState, ActionType<typeof ac>>(
    INITIAL_INTENT_MANAGEMENT_STATE,
)
    .handleAction(
        ac.fetchIntentCategories.request,
        (state): IntentManagementState => ({
            ...state,
            isCategoriesFetching: true,
            isCategoriesFetched: false,
        }),
    )
    .handleAction(
        ac.fetchIntentCategories.failure,
        (state, { payload }): IntentManagementState => ({
            ...state,
            isCategoriesFetching: false,
            isCategoriesFetched: false,
            categoryFetchError: payload,
        }),
    )
    .handleAction(
        ac.fetchIntentCategories.success,
        (state, { payload }): IntentManagementState => ({
            ...state,
            isCategoriesFetching: false,
            isCategoriesFetched: true,
            categories: payload.categories,
        }),
    )
    .handleAction(
        ac.fetchIntentTopics.request,
        (state): IntentManagementState => ({
            ...state,
            isTopicsFetching: true,
            isTopicsFetched: false,
        }),
    )
    .handleAction(
        ac.fetchIntentTopics.failure,
        (state, { payload }): IntentManagementState => ({
            ...state,
            isTopicsFetching: false,
            isTopicsFetched: false,
            topicsFetchError: payload,
        }),
    )
    .handleAction(
        ac.fetchIntentTopics.success,
        (state, { payload }): IntentManagementState => ({
            ...state,
            isTopicsFetching: false,
            isTopicsFetched: true,
            topics: payload.topics,
            totalCount: payload.totalCount,
        }),
    )
    .handleAction(
        ac.fetchIntentUserAdmins.request,
        (state): IntentManagementState => ({
            ...state,
            contactAdmins: [],
        }),
    )
    .handleAction(
        ac.fetchIntentUserAdmins.failure,
        (state, { payload }): IntentManagementState => ({
            ...state,
            contactAdmins: [],
        }),
    )
    .handleAction(
        ac.fetchIntentUserAdmins.success,
        (state, { payload }): IntentManagementState => ({
            ...state,
            contactAdmins: payload,
        }),
    )
    .handleAction(
        ac.fetchSelectedIntentTopics.request,
        (state): IntentManagementState => ({
            ...state,
            isSelectedTopicsFetching: true,
            isSelectedTopicsFetched: false,
        }),
    )
    .handleAction(ac.fetchSelectedIntentTopics.success, (state, { payload }): IntentManagementState => {
        const hasManageForSimpleUser =
            salesSettingsHelper.hasAccessToManageIntentTopics() &&
            (!payload.createdAt ||
                (payload.createdAt &&
                    getDifferenceDateByDay(new Date(payload.createdAt), new Date()) > DEFAULT_LOCKED_PERIOD));

        return {
            ...state,
            selectedTopics: payload.topics || [],
            isSelectedTopicsFetching: false,
            selectedTopicsFetchError: undefined,
            createdAt: payload.createdAt ? new Date(payload.createdAt) : undefined,
            isLocked: hasAccessToManageIntentTopicsForAdmin() || hasManageForSimpleUser ? false : true,
        };
    })
    .handleAction(
        ac.fetchSelectedIntentTopics.failure,
        (state, { payload }): IntentManagementState => ({
            ...state,
            isSelectedTopicsFetching: false,
            selectedTopicsFetchError: payload,
            createdAt: undefined,
            selectedTopics: [],
        }),
    )

    .handleAction(
        ac.fetchIntentManagementApproval.request,
        (state): IntentManagementState => ({
            ...state,
            isUserApprovedTerms: false,
            isUserApprovedTermsFetched: false,
        }),
    )
    .handleAction(
        ac.fetchIntentManagementApproval.success,
        (state, { payload }): IntentManagementState => ({
            ...state,
            isUserApprovedTerms: payload.isApproved,
            isUserApprovedTermsFetched: true,
        }),
    )
    .handleAction(
        ac.fetchIntentManagementApproval.failure,
        (state, { payload }): IntentManagementState => ({
            ...state,
            isUserApprovedTerms: false,
        }),
    )
    .handleAction(
        ac.saveIntentManagementApproval.failure,
        (state, { payload }): IntentManagementState => ({
            ...state,
            isUserApprovedTerms: false,
        }),
    )
    .handleAction(
        ac.saveIntentManagementApproval.success,
        (state, { payload }): IntentManagementState => ({
            ...state,
            isUserApprovedTerms: true,
        }),
    )
    .handleAction(
        ac.saveIntentTopics.success,
        (state, { payload }): IntentManagementState => ({
            ...state,
            selectedTopics: state.selectedTopics.map((topic: Topic) => {
                delete topic.isNotApplied;

                return topic;
            }),
            isLocked:
                !hasAccessToManageIntentTopicsForAdmin() &&
                payload.appliedAt &&
                getDifferenceDateByDay(new Date(payload.appliedAt), new Date()) < DEFAULT_LOCKED_PERIOD,
            saveTopicsError: undefined,
        }),
    )
    .handleAction(
        ac.saveIntentTopics.failure,
        (state, { payload }): IntentManagementState => ({
            ...state,
            isLocked: !(salesSettingsHelper.hasAccessToManageIntentTopics() || hasAccessToManageIntentTopicsForAdmin()),
            saveTopicsError: payload,
        }),
    )
    .handleAction(
        ac.setSaveTopicsError,
        (state, { payload }): IntentManagementState => ({
            ...state,
            saveTopicsError: payload,
        }),
    )
    .handleAction(
        ac.setSearchObjectCategories,
        (state, { payload }): IntentManagementState => ({
            ...state,
            searchObj: { ...state.searchObj, categories: payload },
        }),
    )
    .handleAction(
        ac.setFullSearchObject,
        (state, { payload }): IntentManagementState => ({
            ...state,
            searchObj: payload,
        }),
    )
    .handleAction(
        ac.setSelectedTopics,
        (state, { payload }): IntentManagementState => ({
            ...state,
            selectedTopics: payload,
            isScrollSelectedTopic: payload.length > state.selectedTopics.length,
        }),
    );

export default intentManagementReducer;
